<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { CsvPatchLeadMappingEnum as DP_FIELDS } from '@/apis/generated/dialbunny';
import { useI18N } from '@dialpad/i18n';
import SearchDropdown from '@/app/components/SearchDropdown/SearchDropdown.vue';
import type {
  Item,
  ItemGroup,
} from '@/app/components/SearchDropdown/SearchDropdown.vue';
import { DATA_QA } from './csv-dialpad-property-select.constants';
import logger from '@/logger';

const { $t, $ta } = useI18N();

type Props = {
  required?: boolean;
  label?: string;
  disabled?: boolean;
  row?: number;
};

const props = withDefaults(defineProps<Props>(), {
  required: false,
  label: '',
  disabled: false,
  row: 0,
});
const log = computed(() =>
  logger.getChild(`CsvDialpadPropertySelect:${props.row}`),
);
log.value.debug('Mounting');

onMounted(() => {
  log.value.debug('Mounted');
});

const emit = defineEmits<(e: 'update:error', value: string | null) => void>();

const model = defineModel<DP_FIELDS | string | null>({
  default: null,
});

const CONTACT_PROPS: DP_FIELDS[] = [
  DP_FIELDS.JobTitle,
  DP_FIELDS.Industry,
  DP_FIELDS.Department,
  DP_FIELDS.Lang,
  DP_FIELDS.Timezone,
];
const COMMUNICATION_PROPS: DP_FIELDS[] = [
  DP_FIELDS.PrimaryEmail,
  DP_FIELDS.SecondaryEmail,
  DP_FIELDS.SecondaryPhone,
];
const ADDRESS_PROPS: DP_FIELDS[] = [
  DP_FIELDS.AddressName,
  DP_FIELDS.AddressLine1,
  DP_FIELDS.AddressLine2,
  DP_FIELDS.City,
  DP_FIELDS.Region,
  DP_FIELDS.PostalCode,
  DP_FIELDS.Country,
];
type CUSTOM_FIELDS = 'record_status' | 'salutation' | 'custom_lead_source';
const CUSTOM_FIELDS_PROPS: CUSTOM_FIELDS[] = [
  'record_status',
  'salutation',
  'custom_lead_source',
];

const value = ref<Item<DP_FIELDS | string> | null>(null);
const fieldsI18n = $ta('CSV_DIALPAD_PROPERTY_SELECT');
const filter = ref('');
const fieldMap = <T extends string>(field: T): Item<T> => ({
  key: field,
  title: (fieldsI18n[field] as string | undefined) ?? field,
  ref: field,
});
const fieldFilter = <T extends string>(item: Item<T>) =>
  item.title.toLowerCase().includes(filter.value.toLowerCase());

const groups = computed(() => {
  return [
    {
      title: $t('CSV_DIALPAD_PROPERTY_SELECT_CONTACT_INFORMATION'),
      items: CONTACT_PROPS.map(fieldMap).filter(fieldFilter),
    } as ItemGroup<DP_FIELDS>,
    {
      title: $t('CSV_DIALPAD_PROPERTY_SELECT_COMMUNICATION_INFORMATION'),
      items: COMMUNICATION_PROPS.map(fieldMap).filter(fieldFilter),
    } as ItemGroup<DP_FIELDS>,
    {
      title: $t('CSV_DIALPAD_PROPERTY_SELECT_ADDRESS_INFORMATION'),
      items: ADDRESS_PROPS.map(fieldMap).filter(fieldFilter),
    } as ItemGroup<DP_FIELDS>,
    {
      title: $t('CSV_DIALPAD_PROPERTY_SELECT_CUSTOM_FIELDS'),
      items: CUSTOM_FIELDS_PROPS.map(fieldMap).filter(fieldFilter),
    } as ItemGroup<CUSTOM_FIELDS>,
  ];
});

const staticGroups = ref<ItemGroup<DP_FIELDS>[]>([
  {
    items: [
      {
        key: DP_FIELDS.DoNotImport,
        title: fieldsI18n[DP_FIELDS.DoNotImport],
        ref: DP_FIELDS.DoNotImport,
      } as Item<DP_FIELDS>,
    ],
  },
]);

watch(
  [model],
  () => {
    if (value.value?.ref === model.value) return;
    value.value = model.value
      ? {
          key: model.value,
          title: fieldsI18n[model.value] ?? model.value,
          ref: model.value,
        }
      : null;
    log.value.debug('model update', model.value);
  },
  { immediate: true },
);

watch([value], () => {
  log.value.debug('value update', value.value);
  model.value = value.value?.ref ?? null;
});
</script>
<template>
  <div :data-qa="DATA_QA.ROOT">
    <SearchDropdown
      v-model="value"
      :label="label"
      :messages="$ta('CSV_DIALPAD_PROPERTY_SELECT_MESSAGES')"
      :list-aria-label="$t('CSV_DIALPAD_PROPERTY_SELECT_LIST_ARIA_LABEL')"
      :groups="groups"
      :disabled="disabled"
      :static-groups="staticGroups"
      :debounce="0"
      :required="required"
      :placeholder="$t('CSV_DIALPAD_PROPERTY_SELECT_PLACEHOLDER')"
      :error-messages="$ta('CSV_DIALPAD_PROPERTY_SELECT_ERROR_MESSAGES')"
      @update:search="(value) => (filter = value)"
      @update:error="(value) => emit('update:error', value)"
    />
  </div>
</template>
